import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, 
  Loader2, ArrowLeft, FileDown, RefreshCw, Building2, Users, 
  CreditCard, Calendar, AlertTriangle, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DataImport = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('import');
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }
    
    setSelectedFile(file);
    setError(null);
    setPreview(null);
    setImportResult(null);
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${BACKEND_URL}/api/data/import/preview`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to preview file');
      }
      
      const data = await response.json();
      setPreview(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const executeImport = async () => {
    if (!selectedFile) return;
    
    setImporting(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await fetch(`${BACKEND_URL}/api/data/import/execute`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Import failed');
      }
      
      const result = await response.json();
      setImportResult(result);
      setPreview(null);
      setSelectedFile(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/data/export/all`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qsecdef_backup_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/data/export/template`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to download template');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError(err.message);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setPreview(null);
    setImportResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <AppLayout>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className={`p-2 ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
          >
            <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Data Import / Export
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Import historical data or backup your system
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg mb-6`}>
          <div className={`flex gap-1 p-1 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            <button
              onClick={() => setActiveTab('import')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'import'
                  ? 'bg-[#A2FF00] text-black'
                  : `${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`
              }`}
            >
              <Upload className="w-4 h-4" />
              Import Data
            </button>
            <button
              onClick={() => setActiveTab('export')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'export'
                  ? 'bg-[#A2FF00] text-black'
                  : `${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`
              }`}
            >
              <Download className="w-4 h-4" />
              Export / Backup
            </button>
          </div>

          <div className="p-6">
            {/* Import Tab */}
            {activeTab === 'import' && (
              <div className="space-y-6">
                {/* Instructions */}
                <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'} border`}>
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className={`font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>CSV Import Format</h4>
                      <p className={`text-sm mt-1 ${isDark ? 'text-blue-200/80' : 'text-blue-700'}`}>
                        Your CSV should include: company_name, contact_name, contact_email, contact_phone, 
                        contract_value_gbp, billing_type (annual/monthly), contract_start_date (YYYY-MM-DD), 
                        payment_amount, payment_date, payment_method, notes
                      </p>
                      <p className={`text-sm mt-2 ${isDark ? 'text-blue-200/80' : 'text-blue-700'}`}>
                        <strong>Renewal dates are auto-calculated:</strong> Annual contracts renew 12 months from start date.
                      </p>
                      <button
                        onClick={downloadTemplate}
                        className={`mt-3 text-sm flex items-center gap-1 ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
                      >
                        <FileDown className="w-4 h-4" />
                        Download Template CSV
                      </button>
                    </div>
                  </div>
                </div>

                {/* File Upload */}
                {!preview && !importResult && (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                      isDark 
                        ? 'border-white/20 hover:border-[#A2FF00]/50 bg-white/5' 
                        : 'border-gray-300 hover:border-green-500 bg-gray-50'
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {uploading ? (
                      <Loader2 className="w-12 h-12 text-[#A2FF00] animate-spin mx-auto" />
                    ) : (
                      <>
                        <FileSpreadsheet className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                        <p className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Drop your CSV file here or click to browse
                        </p>
                        <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Supports .csv files with historical company and payment data
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <div className="flex items-center gap-2 text-red-400">
                      <AlertCircle className="w-5 h-5" />
                      <span>{error}</span>
                    </div>
                  </div>
                )}

                {/* Preview */}
                {preview && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Import Preview
                      </h3>
                      <Button variant="outline" size="sm" onClick={resetImport}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Choose Different File
                      </Button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border`}>
                        <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{preview.total_rows}</p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Rows</p>
                      </div>
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'} border`}>
                        <p className="text-2xl font-bold text-green-400">{preview.valid_rows}</p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Valid Rows</p>
                      </div>
                      {preview.invalid_rows > 0 && (
                        <div className={`p-4 rounded-lg ${isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'} border`}>
                          <p className="text-2xl font-bold text-red-400">{preview.invalid_rows}</p>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Invalid Rows</p>
                        </div>
                      )}
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'} border`}>
                        <p className="text-2xl font-bold text-blue-400">{preview.new_companies}</p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>New Companies</p>
                      </div>
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-yellow-50 border-yellow-200'} border`}>
                        <p className="text-2xl font-bold text-yellow-400">{preview.existing_companies}</p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Existing (Update)</p>
                      </div>
                    </div>

                    {/* Errors */}
                    {preview.errors.length > 0 && (
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'} border`}>
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-5 h-5 text-red-400" />
                          <span className={`font-medium ${isDark ? 'text-red-300' : 'text-red-800'}`}>
                            {preview.errors.length} rows have errors and will be skipped
                          </span>
                        </div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {preview.errors.slice(0, 5).map((err, idx) => (
                            <p key={idx} className={`text-sm ${isDark ? 'text-red-200/80' : 'text-red-700'}`}>
                              Row {err.row} ({err.company}): {err.errors.join(', ')}
                            </p>
                          ))}
                          {preview.errors.length > 5 && (
                            <p className={`text-sm ${isDark ? 'text-red-200/60' : 'text-red-600'}`}>
                              ... and {preview.errors.length - 5} more
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Preview Table */}
                    <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-lg overflow-hidden`}>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className={isDark ? 'bg-white/5' : 'bg-gray-50'}>
                            <tr>
                              <th className={`text-left p-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Company</th>
                              <th className={`text-left p-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Contact</th>
                              <th className={`text-left p-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Contract</th>
                              <th className={`text-left p-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Payment</th>
                              <th className={`text-left p-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.preview_data.map((row, idx) => (
                              <tr key={idx} className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                                <td className="p-3">
                                  <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{row.company_name}</div>
                                </td>
                                <td className="p-3">
                                  <div className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                                    {row.contact_first_name} {row.contact_last_name}
                                  </div>
                                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{row.contact_email}</div>
                                </td>
                                <td className="p-3">
                                  <div className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                                    £{row.contract_value_gbp?.toLocaleString()}
                                  </div>
                                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {row.billing_type} • {row.contract_start_date}
                                  </div>
                                </td>
                                <td className="p-3">
                                  {row.payment_amount > 0 ? (
                                    <>
                                      <div className={isDark ? 'text-[#A2FF00]' : 'text-green-600'}>
                                        £{row.payment_amount?.toLocaleString()}
                                      </div>
                                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {row.payment_date}
                                      </div>
                                    </>
                                  ) : (
                                    <span className="text-gray-500">-</span>
                                  )}
                                </td>
                                <td className="p-3">
                                  {row.is_valid ? (
                                    <span className="inline-flex items-center gap-1 text-green-400">
                                      <CheckCircle2 className="w-4 h-4" />
                                      Valid
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-red-400">
                                      <AlertCircle className="w-4 h-4" />
                                      Error
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Import Button */}
                    <div className="flex justify-end gap-3">
                      <Button variant="outline" onClick={resetImport}>
                        Cancel
                      </Button>
                      <Button
                        onClick={executeImport}
                        disabled={importing || preview.valid_rows === 0}
                        className="bg-[#A2FF00] text-black hover:bg-[#8EE000]"
                      >
                        {importing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Import {preview.valid_rows} Records
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Import Result */}
                {importResult && (
                  <div className="space-y-4">
                    <div className={`p-6 rounded-lg ${isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-50 border-green-200'} border text-center`}>
                      <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
                      <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Import Completed Successfully!
                      </h3>
                      <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Your historical data has been imported.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border`}>
                        <div className="flex items-center gap-3">
                          <Building2 className="w-8 h-8 text-blue-400" />
                          <div>
                            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {importResult.companies_created}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Companies Created</p>
                          </div>
                        </div>
                      </div>
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border`}>
                        <div className="flex items-center gap-3">
                          <Users className="w-8 h-8 text-yellow-400" />
                          <div>
                            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {importResult.companies_updated}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Companies Updated</p>
                          </div>
                        </div>
                      </div>
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border`}>
                        <div className="flex items-center gap-3">
                          <Calendar className="w-8 h-8 text-purple-400" />
                          <div>
                            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {importResult.contracts_created}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Contracts Created</p>
                          </div>
                        </div>
                      </div>
                      <div className={`p-4 rounded-lg ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border`}>
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-8 h-8 text-green-400" />
                          <div>
                            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {importResult.payments_created}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Payments Recorded</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-center gap-3">
                      <Button variant="outline" onClick={resetImport}>
                        Import More Data
                      </Button>
                      <Button
                        onClick={() => navigate('/nurturing')}
                        className="bg-[#A2FF00] text-black hover:bg-[#8EE000]"
                      >
                        View Companies
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Export Tab */}
            {activeTab === 'export' && (
              <div className="space-y-6">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'} border`}>
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className={`font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>System Backup</h4>
                      <p className={`text-sm mt-1 ${isDark ? 'text-blue-200/80' : 'text-blue-700'}`}>
                        Export all your companies, contacts, contracts, and payment records to a CSV file. 
                        This file can be used for backup purposes or to import into another system.
                      </p>
                    </div>
                  </div>
                </div>

                <div className={`p-8 rounded-lg ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border text-center`}>
                  <Download className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                  <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Export All Data
                  </h3>
                  <p className={`mt-2 mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Download a complete backup of your CRM data in CSV format
                  </p>
                  <Button
                    onClick={exportData}
                    disabled={exporting}
                    className="bg-[#A2FF00] text-black hover:bg-[#8EE000]"
                  >
                    {exporting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Preparing Download...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download Backup CSV
                      </>
                    )}
                  </Button>
                </div>

                <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-100 border-gray-200'} border`}>
                  <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>What&apos;s included in the export:</h4>
                  <ul className={`text-sm space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <li>• All companies and their details</li>
                    <li>• Contact information (name, email, phone)</li>
                    <li>• Contract values and billing types</li>
                    <li>• Contract start dates and renewal dates</li>
                    <li>• All payment records with amounts and dates</li>
                    <li>• Company status (lead, member, etc.)</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default DataImport;
